import React from 'react';
import { X } from '@phosphor-icons/react';
import { MarketplaceFilters } from '../../../lib/marketplaceFilters';
import { LOCATION_TREE } from '../../../lib/constants';
interface ActiveFilterChipsProps {
  filters: MarketplaceFilters;
  onRemove: (key: keyof MarketplaceFilters, value?: string) => void;
  onClearAll: () => void;
}
export function ActiveFilterChips({
  filters,
  onRemove,
  onClearAll
}: ActiveFilterChipsProps) {
  const chips: {
    label: string;
    onRemove: () => void;
  }[] = [];
  // Categories
  filters.categories.forEach(cat => {
    const parts = cat.split('|');
    const label = parts.length > 1 ? `${parts[0]} · ${parts[1]}` : parts[0];
    chips.push({
      label,
      onRemove: () => onRemove('categories', cat)
    });
  });
  // Locations — collapse to the highest fully-selected ancestor so a fully
  // selected city shows ONE chip instead of dumping all its zones.
  const selectedLocSet = new Set(filters.locations);
  const getLocLeaves = (node: any): string[] => {
    if (!node.children) return [node.value];
    return node.children.flatMap((c: any) => getLocLeaves(c));
  };
  const walkLoc = (node: any) => {
    const leaves = getLocLeaves(node);
    if (leaves.length === 0) return;
    const allSelected = leaves.every(l => selectedLocSet.has(l));
    if (allSelected) {
      chips.push({
        label: node.label,
        onRemove: () => {
          const leafSet = new Set(leaves);
          // Emit one removal per leaf — onRemove signature takes a single value,
          // so we clear them by re-dispatching for each.
          leaves.forEach(l => onRemove('locations', l));
        }
      });
      return;
    }
    if (node.children) node.children.forEach((child: any) => walkLoc(child));
  };
  LOCATION_TREE.forEach((root: any) => walkLoc(root));
  // Budget
  if (filters.minBudgetEUR || filters.maxBudgetEUR) {
    const label = filters.minBudgetEUR && filters.maxBudgetEUR ? `${filters.minBudgetEUR}-${filters.maxBudgetEUR} €` : filters.minBudgetEUR ? `Min ${filters.minBudgetEUR} €` : `Max ${filters.maxBudgetEUR} €`;
    chips.push({
      label,
      onRemove: () => {
        onRemove('minBudgetEUR');
        onRemove('maxBudgetEUR');
      }
    });
  }
  // Move In
  if (filters.moveIn !== 'any') {
    const label = filters.moveIn === 'specific' && filters.moveInSpecificMonth ? filters.moveInSpecificMonth : filters.moveIn === 'immediate' ? 'Imediat' : 'În 3 luni';
    chips.push({
      label,
      onRemove: () => onRemove('moveIn')
    });
  }
  // Configuration
  filters.configuration.forEach(c => {
    const label = c === 'singur' ? 'Singur' : c === 'cuplu' ? 'Cuplu' : c === 'familie' ? 'Familie' : 'Combo-Rent';
    chips.push({
      label,
      onRemove: () => onRemove('configuration', c)
    });
  });
  // Lifestyle gates
  if (filters.noSmokers) {
    chips.push({
      label: 'Doar nefumători',
      onRemove: () => onRemove('noSmokers')
    });
  }
  if (filters.noPets) {
    chips.push({
      label: 'Doar fără animale',
      onRemove: () => onRemove('noPets')
    });
  }
  // Duration
  filters.duration.forEach(d => {
    const label = d === '1y' ? '1 an' : d === '2y' ? '2 ani' : d === 'short' ? 'Termen scurt' : 'Nedeterminat';
    chips.push({
      label,
      onRemove: () => onRemove('duration', d)
    });
  });
  // Verifications
  if (filters.verifiedOnly) {
    chips.push({
      label: 'Verificați complet',
      onRemove: () => onRemove('verifiedOnly')
    });
  }
  if (filters.incomeVerifiedOnly) {
    chips.push({
      label: 'Venit verificat',
      onRemove: () => onRemove('incomeVerifiedOnly')
    });
  }
  // Rating
  if (filters.minRating > 0) {
    chips.push({
      label: `Rating ≥ ${filters.minRating}`,
      onRemove: () => onRemove('minRating')
    });
  }
  if (chips.length === 0) return null;
  return <div className="flex flex-wrap items-center gap-2 py-3" data-id="element-2287">
      {chips.map((chip, i) => <span key={i} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-dark-3 border border-dark-4 text-sm text-light-2" data-id="element-2288">
          {chip.label}
          <button type="button" onClick={chip.onRemove} className="hover:text-light-1 transition-colors" aria-label="Elimină filtru" data-id="element-2289">
            <X size={12} weight="bold" data-id="element-2290" />
          </button>
        </span>)}
      <button type="button" onClick={onClearAll} className="text-sm text-light-3 hover:text-light-1 transition-colors ml-2" data-id="element-2291">
        Șterge toate
      </button>
    </div>;
}